import { Component } from 'react'
import type { CSSProperties, ErrorInfo, JSX, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { TEMP_LANG_KEY } from '@rs/ui'

/**
 * ══ LƯỚI AN TOÀN: MỘT LỖI VẼ KHÔNG LÀM TRẮNG CẢ ỨNG DỤNG (T3, soát 16/10) ═════════════════════════
 * Chép `proship-trans/src/components/ErrorBoundary.tsx` — câu chữ, ba nút, khung đỏ có stack để chép
 * gửi dev. Bản cũ bọc HAI tầng: quanh `<Outlet />` trong khung (`Layout.tsx:951`, giữ header + menu
 * để thoát) và quanh cả `<App />` (`main.tsx:28`). rs-erp làm y vậy ở `app/router.tsx`.
 *
 * Trước đây rs-erp không có tầng nào: một dòng `null` lạ từ máy chủ, hay một chunk 404 sau lượt
 * deploy, là `#root` trống trơn — không menu, không nút Đăng xuất, không biết báo gì cho dev.
 *
 * ⭐ THÊM SO VỚI BẢN CŨ — CHUNK MẤT SAU DEPLOY: màn nạp trễ (`React.lazy`) trỏ tới tệp có mã băm;
 *   deploy xong tệp cũ biến mất ⇒ "Failed to fetch dynamically imported module". Bản cũ không nạp
 *   trễ nên không gặp. Lỗi này TẢI LẠI TRANG là hết (lấy `index.html` mới), nên tự tải lại MỘT lần;
 *   lần thứ hai trong vòng 30 giây thì thôi, hiện khung lỗi — tránh vòng lặp tải lại vô tận khi tệp
 *   mất thật.
 */

const CHUNK_ERR = /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i
const RELOAD_MARK = 'rs:chunk-reload-at'

/** Lỗi nạp chunk ⇒ tải lại trang một lần (đánh dấu ở `sessionStorage`). Trả `true` khi đã tải lại. */
export const reloadOnceForChunkError = (error: Error): boolean => {
  if (!CHUNK_ERR.test(error.message)) return false
  try {
    const last = Number(sessionStorage.getItem(RELOAD_MARK) ?? '0')
    if (Date.now() - last < 30_000) return false
    sessionStorage.setItem(RELOAD_MARK, String(Date.now()))
  } catch { return false }
  location.reload()
  return true
}

const btn = (bg: string): CSSProperties => ({
  background: bg, color: '#fff', border: 0, borderRadius: 8, padding: '8px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer',
})

/** Khung lỗi — hàm riêng để dùng được `useTranslation` (lớp `Component` không gọi hook được). */
const Fallback = ({ error, onReset }: { error: Error; onReset: () => void }): JSX.Element => {
  const { t } = useTranslation()
  const clearAndReload = (): void => {
    if (!window.confirm(t('common.errBoundaryClearConfirm'))) return
    // Chỉ xoá khoá của rs-erp (`rs:`) và GIỮ ngôn ngữ đã chọn — bản cũ cũng giữ `proship:lang`.
    try {
      Object.keys(localStorage).filter((k) => k.startsWith('rs:') && k !== TEMP_LANG_KEY).forEach((k) => { localStorage.removeItem(k) })
    } catch { /* chế độ riêng tư */ }
    location.reload()
  }
  return (
    <div role="alert" style={{ maxWidth: 820, margin: '40px auto', padding: 24, fontFamily: 'system-ui, sans-serif', color: '#0f172a' }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#b91c1c', marginBottom: 8 }}>{t('common.errBoundaryTitle')}</div>
      <div style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>{t('common.errBoundaryHint')}</div>
      <pre style={{ background: '#0f172a', color: '#fca5a5', padding: 14, borderRadius: 10, fontSize: 12, overflow: 'auto', maxHeight: 320, whiteSpace: 'pre-wrap' }}>
        {error.message}
        {'\n'}{error.stack}
      </pre>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => { location.reload() }} style={btn('#0f172a')}>{t('common.errBoundaryReload')}</button>
        <button type="button" onClick={onReset} style={btn('#0ea5e9')}>{t('common.errBoundaryRetry')}</button>
        <button type="button" onClick={clearAndReload} style={btn('#b91c1c')}>{t('common.errBoundaryClear')}</button>
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<{ children: ReactNode; label?: string }, { error: Error | null }> {
  override state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error): { error: Error } { return { error } }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', this.props.label ?? '', error, info)
    reloadOnceForChunkError(error)
  }

  reset = (): void => { this.setState({ error: null }) }

  override render(): ReactNode {
    const e = this.state.error
    if (e === null) return this.props.children
    return <Fallback error={e} onReset={this.reset} />
  }
}
